/**
 * LoginPage — Google OAuth sign-in.
 *
 * Asks the backend to open Google consent in the system browser via
 * Python's webbrowser.open() (works reliably in PyWebView on all
 * platforms). Falls back to window.open() in dev/browser mode.
 *
 * Polling detects the session server-side. If it doesn't land within
 * 10s, shows a manual "I've signed in" button + the OAuth URL for
 * copy-paste.
 */

import { useState, useEffect, useRef } from "react";
import { useAuthStore } from "@/stores/authStore";

export function LoginPage() {
  const { error, isLoading, checkSession } = useAuthStore();
  const [waitingForAuth, setWaitingForAuth] = useState(false);
  const [manualUrl, setManualUrl] = useState<string | null>(null);
  const [showFallback, setShowFallback] = useState(false);
  const [manualChecking, setManualChecking] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    };
  }, []);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (fallbackTimerRef.current) { clearTimeout(fallbackTimerRef.current); fallbackTimerRef.current = null; }
  };

  const handleLogin = async () => {
    setManualUrl(null);
    setCopied(false);
    setShowFallback(false);

    // Ask the backend to open Google OAuth in the system browser.
    let url: string | null = null;
    try {
      const res = await fetch("/api/auth/google/open-browser", { method: "POST" });
      const data = await res.json();
      // Hosted cloud: navigate THIS tab to Google. The callback stores tokens
      // in localStorage and redirects back to the app — no system browser,
      // no session polling.
      if (data.redirect && data.url) {
        window.location.href = data.url;
        return;
      }
      url = data.url ?? null;
    } catch {
      // Fallback: try window.open() directly (works in dev/browser mode)
      window.open("/api/auth/google/start", "_blank", "noopener");
    }

    setWaitingForAuth(true);

    // After 10s, show fallback options if polling hasn't detected the session
    fallbackTimerRef.current = setTimeout(() => {
      setShowFallback(true);
      if (url) setManualUrl(url);
    }, 10000);

    // Poll for the session every 2s
    pollRef.current = setInterval(async () => {
      const success = await checkSession();
      if (success) {
        stopPolling();
        setWaitingForAuth(false);
        setManualUrl(null);
        setShowFallback(false);
      }
    }, 2000);
  };

  /** Manual "I've signed in" button — one-shot session check with feedback. */
  const handleManualCheck = async () => {
    setManualChecking(true);
    setManualError(null);
    try {
      const success = await checkSession();
      if (success) {
        stopPolling();
        setWaitingForAuth(false);
        setManualUrl(null);
        setShowFallback(false);
      } else {
        setManualError("No active session found. Please complete sign-in in the browser first.");
      }
    } catch (e) {
      setManualError(`Connection error: ${(e as Error).message}`);
    } finally {
      setManualChecking(false);
    }
  };

  const handleCopy = async () => {
    if (!manualUrl) return;
    try {
      await navigator.clipboard.writeText(manualUrl);
    } catch {
      const input = document.createElement("input");
      input.value = manualUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetLogin = () => {
    stopPolling();
    setWaitingForAuth(false);
    setManualUrl(null);
    setShowFallback(false);
    setCopied(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-jove-10 via-surface to-jove-30">
      <div className="w-full max-w-sm p-8 bg-surface rounded-2xl shadow-card text-center">
        {/* Logo */}
        <div className="mb-6">
          <img src="/cowork-logo.svg" alt="Nbyula OmniWerk" className="block w-16 h-16 rounded-2xl mb-4 mx-auto" />
          <h1 className="text-2xl font-bold text-space">Nbyula OmniWerk</h1>
        </div>

        {/* Description */}
        <p className="text-sm text-space-50 mb-8">
          Sign in with your company account to get started.
        </p>

        {/* Sign in button */}
        <button
          onClick={handleLogin}
          disabled={isLoading || waitingForAuth}
          className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-space-30 rounded-lg
                     hover:bg-space-20 transition-colors disabled:opacity-50"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span className="text-sm font-medium text-space">
            {waitingForAuth ? "Waiting for sign-in..." : isLoading ? "Signing in..." : "Sign in with Google"}
          </span>
        </button>

        {waitingForAuth && (
          <div className="mt-4 space-y-3">
            {/* Initial waiting state */}
            {!showFallback && (
              <p className="text-xs text-space-40">
                Complete the sign-in in the browser window that opened.
              </p>
            )}

            {/* Fallback: shown after 10s if polling hasn't detected login */}
            {showFallback && (
              <div className="p-3 bg-space-10 rounded-lg text-left space-y-3">
                <p className="text-xs text-space-50">
                  Already signed in? Click below to check:
                </p>

                {/* Manual check button */}
                <button
                  onClick={handleManualCheck}
                  disabled={manualChecking}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium
                             text-white bg-jove rounded-lg hover:bg-jove-80 transition-colors disabled:opacity-50"
                >
                  {manualChecking ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                      Checking...
                    </>
                  ) : (
                    "I've signed in — check now"
                  )}
                </button>

                {manualError && (
                  <p className="text-xs text-hydra">{manualError}</p>
                )}

                {/* Copy URL fallback */}
                {manualUrl && (
                  <div className="space-y-2 pt-2 border-t border-space-20">
                    <p className="text-xs text-space-40">
                      Browser didn't open? Copy this URL:
                    </p>
                    <div className="flex gap-2 items-start">
                      <code className="flex-1 text-xs font-mono text-space break-all bg-surface px-2 py-1.5 rounded border border-space-20 max-h-16 overflow-y-auto select-all">
                        {manualUrl}
                      </code>
                      <button
                        onClick={handleCopy}
                        className="shrink-0 px-2.5 py-1.5 text-xs font-medium text-jove border border-jove rounded hover:bg-jove-10 transition-colors"
                      >
                        {copied ? "Copied!" : "Copy"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={resetLogin}
              className="text-xs text-jove hover:underline"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mt-4 p-3 bg-hydra-50 text-hydra text-sm rounded-lg">
            {error}
          </div>
        )}

        {/* Domain hint */}
        <p className="text-xs text-space-40 mt-6">
          Only <span className="font-medium">@navigus.in</span> accounts are allowed
        </p>
      </div>
    </div>
  );
}
